import { useState, useCallback, useRef } from 'react'
import { fetchYouTubeTranslation } from '@/services/youtube.service'
import { decodeHtmlEntities } from '@/utils/textUtils'
import type { YouTubeTranslationsResponse } from '@/types/youtubeTranslations.types'

/**
 * Translation fetch lifecycle state for a single segment.
 *
 * - idle: No fetch has been triggered yet (initial state or segment changed).
 * - loading: Fetch in progress.
 * - success: Fetch completed; translationAvailable and translation reflect the result.
 *   Carries source, translationId for edit-mode decisions (parallel to captions hook).
 * - no-translation: No translation track/source available — a valid expected state.
 * - no-transcript: Translation unavailable because no transcript exists for this segment.
 *   Shows guidance to fetch transcript first via the Transcript tab.
 * - error: Upstream failure (quota exceeded, API unavailable, network error).
 */
export type TranslationFetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'success'
      translation: string
      /** DB row ID of the displayed translation row. */
      translationId?: number
      /** 'youtube', 'google_translate', or 'user' indicating priority display rule result. */
      source?: 'youtube' | 'google_translate' | 'user'
      /**
       * The YouTube auto-translation text, present only when source='user' and a
       * youtube row also exists. Enables the panel to show a comparison view.
       */
      youtubeTranslation?: string
    }
  | { status: 'no-translation' }
  | { status: 'no-transcript' }
  | { status: 'quota-exceeded' }
  | { status: 'api-unavailable' }
  | { status: 'error'; message: string }

/**
 * Translation text is stored HTML-escaped in the DB (YouTube caption tracks
 * arrive with &#39;, &quot;, …). Decode once at the response boundary so every
 * consumer — read-only display, comparison column, and edit-mode seed — gets
 * plain text.
 */
const toSuccessState = (response: YouTubeTranslationsResponse): TranslationFetchState => ({
  status: 'success',
  translation: decodeHtmlEntities(response.translation ?? ''),
  translationId: response.translation_id,
  source: response.source,
  youtubeTranslation:
    response.youtube_translation != null
      ? decodeHtmlEntities(response.youtube_translation)
      : undefined,
})

/**
 * useYouTubeTranslations — manages user-initiated translation fetch for a YouTube segment.
 *
 * Mirrors the useYouTubeCaptions hook design:
 * - NOT a TanStack Query managed query — translation fetch is strictly user-initiated.
 * - fetchFromCache auto-fires on segment select (DB-only, fast, no external APIs).
 * - fetchTranslation is called explicitly by the "Get Translation" button.
 * - reset() is called when the selected segment changes.
 *
 * Usage:
 *   const { state, fetchTranslation, fetchFromCache, reset } = useYouTubeTranslations()
 *   <Button onClick={() => fetchTranslation(segment.id)} />
 *
 * When the selected segment changes, call reset() so the panel returns to idle.
 */
export const useYouTubeTranslations = () => {
  const [state, setState] = useState<TranslationFetchState>({ status: 'idle' })

  /**
   * Monotonically increasing counter used to discard stale async responses.
   * Each call to fetchTranslation or fetchFromCache stamps a requestId; the
   * async callback only applies setState when its stampId still matches the
   * current counter value. This prevents rapid segment-switching from causing
   * a previous segment's cache response to clobber the new segment's state.
   */
  const requestCounterRef = useRef(0)

  /**
   * Warm pre-fetch cache: segmentId → resolved TranslationFetchState.
   * Populated by prefetch(); read by fetchFromCache() to skip the loading flash.
   */
  const prefetchCacheRef = useRef<Map<number, TranslationFetchState>>(new Map())

  /**
   * Fire-and-forget pre-fetch for a segment. Stores the resolved state in
   * prefetchCacheRef without touching display state.
   */
  const prefetch = useCallback(async (segmentId: number) => {
    if (prefetchCacheRef.current.has(segmentId)) return
    prefetchCacheRef.current.set(segmentId, { status: 'loading' })

    try {
      const response: YouTubeTranslationsResponse = await fetchYouTubeTranslation({
        segment_id: segmentId,
        cache_only: true,
      })

      if (response.status === 'failed' || !response.translation_available) {
        // Delete rather than caching the miss: a translation fetched later
        // (Get Auto Translate) lands in the DB, and a cached idle entry would
        // permanently mask it from fetchFromCache.
        prefetchCacheRef.current.delete(segmentId)
        return
      }

      prefetchCacheRef.current.set(segmentId, toSuccessState(response))
    } catch {
      prefetchCacheRef.current.delete(segmentId)
    }
  }, [])

  /**
   * Trigger a full translation fetch for the given segment ID
   * (including yt-dlp / Google Translate if needed).
   * Only { segment_id } is sent — timestamps are resolved on the backend.
   */
  const fetchTranslation = useCallback(async (segmentId: number) => {
    const requestId = ++requestCounterRef.current
    setState({ status: 'loading' })

    try {
      const response: YouTubeTranslationsResponse = await fetchYouTubeTranslation({
        segment_id: segmentId,
      })

      // Discard stale responses from superseded segment requests.
      if (requestCounterRef.current !== requestId) return

      if (response.status === 'failed') {
        setState({
          status: 'error',
          message: response.message ?? 'Translation service is unavailable. Please try again.',
        })
        return
      }

      if (!response.translation_available) {
        if (response.reason === 'no_transcript') {
          setState({ status: 'no-transcript' })
        } else {
          setState({ status: 'no-translation' })
        }
        return
      }

      const freshState = toSuccessState(response)
      setState(freshState)
      // Warm the pre-fetch cache so segment re-selects serve the fresh result
      // instead of a stale pre-fetch entry.
      prefetchCacheRef.current.set(segmentId, freshState)
    } catch (err) {
      if (requestCounterRef.current !== requestId) return
      const resp = (err as { response?: { status?: number; data?: { error_code?: string } } })?.response
      if (resp?.status === 503) {
        if (resp.data?.error_code === 'YOUTUBE_API_UNAVAILABLE') {
          setState({ status: 'api-unavailable' })
        } else {
          // SUBSCRIPTION_QUOTA_EXCEEDED or unknown 503
          setState({ status: 'quota-exceeded' })
        }
      } else {
        setState({
          status: 'error',
          message: 'Failed to fetch translation. Please try again.',
        })
      }
    }
  }, [])

  /**
   * Fetch only DB-cached translation for the segment (cache_only=true).
   * yt-dlp and Google Translate are never invoked. On cache miss, stays idle
   * so the "Get Translation" button remains visible and enabled.
   * Call this automatically on segment select.
   */
  const fetchFromCache = useCallback(async (segmentId: number) => {
    const requestId = ++requestCounterRef.current

    // Check warm pre-fetch cache first: apply result synchronously (no loading flash).
    const warmed = prefetchCacheRef.current.get(segmentId)
    if (warmed && warmed.status !== 'loading') {
      setState(warmed)
      return
    }

    setState({ status: 'loading' })

    try {
      const response: YouTubeTranslationsResponse = await fetchYouTubeTranslation({
        segment_id: segmentId,
        cache_only: true,
      })

      // Discard stale responses from superseded segment requests.
      if (requestCounterRef.current !== requestId) return

      if (response.status === 'failed') {
        // On a cache-only call, treat failures as idle rather than error — the
        // user can still manually trigger a full fetch with the button.
        setState({ status: 'idle' })
        return
      }

      // Cache miss: translation_available=false means nothing is cached.
      // Stay idle so the "Get Translation" button is shown.
      if (!response.translation_available) {
        setState({ status: 'idle' })
        return
      }

      const freshState = toSuccessState(response)
      setState(freshState)
      // Update warm cache with fresh result so subsequent auto-selects are instant.
      prefetchCacheRef.current.set(segmentId, freshState)
    } catch {
      if (requestCounterRef.current !== requestId) return
      // Network error on cache-only: silently stay idle.
      setState({ status: 'idle' })
    }
  }, [])

  /** Reset to idle — call this when the selected segment changes. */
  const reset = useCallback(() => {
    setState({ status: 'idle' })
  }, [])

  /** Remove a segment from the warm prefetch cache.
   * Call this after a user translation save so the next fetchFromCache call for
   * that segment goes to the DB instead of returning stale pre-save data.
   */
  const evictFromCache = useCallback((segmentId: number) => {
    prefetchCacheRef.current.delete(segmentId)
  }, [])

  return { state, fetchTranslation, fetchFromCache, prefetch, reset, evictFromCache }
}
